import { useState, useEffect, FormEvent } from "react";
import { Button } from "../../../components/Button";
import { ButtonGroup } from "../../../components/ButtonGroup";
import { Popup } from "../../../components/Popup";
import PopupStructure from "../../../components/PopupStructure";
import { PopupTitle } from "../../../components/PopupTitle";
import SettingsPopupSelectField from "./src/SettingsPopupSelect";
import styles from "./SettingsPopup.module.css";
import cn from "classnames";
import StudioCanvas from "../Canvas";
import { ISettingsPopup } from "./SettingsPopup.interface";
import { useMediaDeviceSelect, useTracks, useRoomContext } from "@livekit/components-react";
import { Track } from "livekit-client";
import { IOption } from "../../../components/SelectField/SelectField.interface";
import InputLegend from "../../../components/InputLegend";
import TextAreaField from "../../../components/TextAreaField";
import { useChangeOrientationStudioMutation, UserStudio, useUpdateBroadcastMutation, useUpdateQualityRoomMutation, useGetUserQuery } from "../../../features/api/apiSlice";
import { Error } from "../../../components/Error";
import SelectField from "../../../components/SelectField";
import ToggleSwitch from "../../../components/ToggleSwitch/ToggleSwitch";
import { Link } from "../../../components/Link";

 const streamingModeOptions: IOption[] = [
    { label: 'Горизонтальный стриминг', value: 'landscape' },
    { label: 'Вертикальный стриминг', value: 'portrait' }
  ];

function SettingsPopup({ isShow, onClose, studio }: ISettingsPopup) {
  const [activeTab, setActiveTab] = useState<'audio-video' | 'general'>('general');
  const [broadcastName, setBroadcastName] = useState<string>('');
  const [broadcastDescription, setBroadcastDescription] = useState<string>('');
  const [streamingModeSearch, setStreamingModeSearch] = useState<string>('');
  const [streamingMode, setStreamingMode] = useState<UserStudio["orientation"]>('landscape');
  const [error, setError] = useState('');
  const broadcastStudio = studio?.broadcast;

  const [updateBroadcast, { isLoading }] = useUpdateBroadcastMutation();
  const [changeOrientation] = useChangeOrientationStudioMutation();
  const [updateQuality] = useUpdateQualityRoomMutation();
  const {data: user} = useGetUserQuery();

  const maxResolutionTariff = user?.tariff.max_studio_resolution === "1080p";
  const canChangeResolution = !!maxResolutionTariff;

  const room = useRoomContext();
  const {
    activeDeviceId: activeAudioInputDeviceId,
    devices: audioInputDevices,
    setActiveMediaDevice: setActiveAudioInputDeviceId
  } = useMediaDeviceSelect({ kind: "audioinput" });
  const {
    activeDeviceId: activeVideoInputDeviceId,
    devices: videoInputDevices,
    setActiveMediaDevice: setActiveVideoInputDeviceId
  } = useMediaDeviceSelect({ kind: "videoinput" });

  useEffect(() => {
    if (!broadcastStudio || !studio) {
      return;
    }

    setBroadcastName(broadcastStudio.name || '');
    setBroadcastDescription(broadcastStudio.description || '');
    setStreamingMode(studio.orientation);
  }, [broadcastStudio, studio?.orientation]);

  const videoTracks = useTracks([Track.Source.Camera]);

  const activeAudioInputDevice = audioInputDevices.find(
    ({ deviceId }) => deviceId === activeAudioInputDeviceId
  );

  const activeVideoInputDevice = videoInputDevices.find(
    ({ deviceId }) => deviceId === activeVideoInputDeviceId
  );

  const videoTrackRef = videoTracks?.find(
    (track) => track?.publication?.track?.mediaStreamTrack.label === activeVideoInputDevice?.label
  );

  function mapDevicesToOptions(devices: { label: string; deviceId: string }[]): IOption[] {
    return devices.map(({ label, deviceId }) => {
      return { label: label || `Устройство ${deviceId.substring(0, 5)}...`, value: deviceId };
    });
  }

  function getVideoAudioDevices(kind: "videoinput" | "audioinput"): IOption[] {
    if (kind === "videoinput" && videoInputDevices) {
      return mapDevicesToOptions(videoInputDevices);
    }

    if (kind === "audioinput" && audioInputDevices) {
      return mapDevicesToOptions(audioInputDevices);
    }

    return [];
  }

  async function setInputDevice(kind: "videoinput" | "audioinput", deviceId: string) {
    if (!deviceId || !room) {
      return;
    }

    try {
      if (kind === "videoinput") {
        setActiveVideoInputDeviceId(deviceId);
        await room.switchActiveDevice('videoinput', deviceId);
      } else if (kind === "audioinput") {
        setActiveAudioInputDeviceId(deviceId);
        await room.switchActiveDevice('audioinput', deviceId);
      }
    } catch (error) {
      console.error(`Ошибка при переключении устройства ${kind}:`, error);
    }
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setBroadcastName(e.target.value);
  };

  const handleDescChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setBroadcastDescription(e.target.value);
  };

  const handleStreamingModeChange = async (option: IOption) => {
    try {
      setStreamingMode(option.value as UserStudio["orientation"]);
      await changeOrientation({ orientation: option.value as 'landscape' | 'portrait' }).unwrap();
    } catch (err) {
      console.error('Ошибка при изменении ориентации стриминга:', err);
      setStreamingMode(studio?.orientation || 'landscape');
    }
  };

  const handleSave = async () => {
    if (!broadcastStudio || !broadcastStudio.id) {
      return;
    }

    if (!broadcastName.trim()) {
      setError('Название эфира не может быть пустым');
      return;
    }

    try {
      const updateData = {
        name: broadcastName,
        description: broadcastDescription,
        start_at: broadcastStudio.start_at || new Date().toISOString(),
        rtmp_key: broadcastStudio.rtmp_key || ''
      };

      await updateBroadcast({
        id: broadcastStudio.id,
        body: updateData
      }).unwrap();

      setError('');
      onClose();
    } catch (err) {
      console.error('Ошибка при обновлении трансляции:', err);
      setError('Произошла ошибка при сохранении данных');
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSave();
  };

  const handleResolutionChange = async (resolution: string) => {
    if (!studio || !studio.id || !room) {
      return;
    }

    try {
      await updateQuality({
        resolution: resolution
      }).unwrap();

      const videoResolution = (() => {
          if (resolution === "1080p") {
              return { width: 1920, height: 1080 };
          }
          return { width: 1280, height: 720 };
      })();

      const videoTrack = room.localParticipant.getTrackPublication(Track.Source.Camera)?.track;

      if (videoTrack) {
        await videoTrack.restartTrack({
          resolution: videoResolution
        });
      }

    } catch (error) {
      console.error('Ошибка при обновлении разрешения:', error);
      setError('Не удалось обновить качество видео');
    }
  };

  return (
    <Popup maxWidth="768px" onClose={onClose} isShow={isShow} >
      <PopupStructure
        headerComponent={
          <div className={styles['settings-popup__header']}>
            <PopupTitle className={styles['settings-popup__title']}>
              Настройки
            </PopupTitle>
            <ButtonGroup className={styles['settings-popup__tabs']}>
              <Button
                kind={activeTab === 'general' ? 'primary' : 'secondary'}
                onClick={() => setActiveTab('general')}
              >
                Основное
              </Button>
              <Button
                kind={activeTab === 'audio-video' ? 'primary' : 'secondary'}
                onClick={() => setActiveTab('audio-video')}
              >
                Аудио и видео
              </Button>
            </ButtonGroup>
          </div>
        }
        contentComponent={
          activeTab === 'audio-video' ? (
            <div className={cn(styles['settings-popup__content__container'])}>
              <div className={cn(styles["settings-popup-select-field__container"])}>
                <SettingsPopupSelectField
                  label="Микрофон"
                  options={getVideoAudioDevices("audioinput")}
                  selectedValue={{
                    label: activeAudioInputDevice?.label ?? "",
                    value: activeAudioInputDevice?.deviceId ?? ""
                  }}
                  setSelectedValue={(deviceId) => { setInputDevice("audioinput", deviceId); }}
                />
                <SettingsPopupSelectField
                  label="Камера"
                  options={getVideoAudioDevices("videoinput")}
                  selectedValue={{
                    label: activeVideoInputDevice?.label ?? "",
                    value: activeVideoInputDevice?.deviceId ?? ""
                  }}
                  setSelectedValue={(deviceId) => { setInputDevice("videoinput", deviceId); }}
                />
              </div>
              <StudioCanvas trackRef={videoTrackRef} />
            </div>
          ) : (
            <>
              <form
                id="settings-form"
                onSubmit={handleSubmit}
                className={styles["settings-popup__general-tab"]}
              >
                <InputLegend legend="Название эфира">
                  <TextAreaField
                    placeholder="Название эфира*"
                    maxLength={100}
                    value={broadcastName}
                    onChange={handleNameChange}
                    usedChars={broadcastName.length}
                    isError={error.includes("Название")}
                  />
                </InputLegend>
                <InputLegend legend="Описание эфира">
                  <TextAreaField
                    placeholder="Описание эфира"
                    maxLength={5000}
                    value={broadcastDescription}
                    onChange={handleDescChange}
                    usedChars={broadcastDescription.length}
                  />
                </InputLegend>
                <InputLegend legend="Режим стриминга">
                  <SelectField
                    placeholder="Выберите режим стриминга"
                    options={streamingModeOptions}
                    selectedOption={streamingModeOptions.find(option => option.value === streamingMode)}
                    selectedOptionHandler={handleStreamingModeChange}
                    value={streamingModeSearch}
                    setValue={setStreamingModeSearch}
                  />
                </InputLegend>
                {error && <Error>{error}</Error>}
              </form>

             <div className={styles["settings-popup__resolution-section"]}>
                <InputLegend legend="Качество видео">
                   <ToggleSwitch
                      options={["720p", "1080p"]}
                      selectedOption={studio?.resolution || "720p"}
                      onChange={(resolution) => handleResolutionChange(resolution)}
                      disabled={!canChangeResolution}
                      tooltipContent={!canChangeResolution ?
                      <div>
                        Для переключения качества видео на 1080p необходимо обновить
                        <Link to="/tariffs"> тариф</Link>
                      </div> : null}
                    />
                </InputLegend>
              </div>
            </>
          )
        }
        footerComponent={
          <ButtonGroup className={styles['settings-popup__footer']}>
            <Button kind="secondary" type="button" onClick={() => onClose()}>Отмена</Button>
            <Button
              kind="primary"
              type="submit"
              form={activeTab === 'general' ? "settings-form" : undefined}
              onClick={activeTab === 'audio-video' ? onClose : undefined}
              disabled={isLoading}
            >
              {isLoading ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </ButtonGroup>
        }
      />
    </Popup>
  );
}

export default SettingsPopup;